import { Component } from '@theme/component';
import { prefersReducedMotion, debounce, clamp } from '@theme/utilities';

/**
 * An endless kinetic drag-to-scroll VIDEO reel.
 *
 * This is the video sibling of <wl-drag-gallery>. It reuses the same proven
 * engine: Pointer Events drive a single mouse/trackpad/touch code path, the
 * track is moved with `transform: translate3d()`, and the original tile set is
 * cloned so the row loops seamlessly in both directions (the visible transform
 * is always wrapped into one set-width, and because the cloned content is
 * identical the wrap is invisible). On release, momentum keeps the row gliding
 * under friction. An optional gentle auto-scroll drifts the row while idle and
 * pauses on hover/drag.
 *
 * On top of the drag engine it adds video lifecycle management: an
 * IntersectionObserver (root = the viewport) lazy-loads each tile's sources the
 * first time it nears the viewport and plays only the tiles currently on screen,
 * pausing everything off-screen — clones included. YouTube/Vimeo tiles render a
 * poster and lazy-load their iframe on tap (no reliable JS pause control).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The clipping viewport (focusable, captures pointers).
 * @property {HTMLElement} track - The flex row that is transformed.
 *
 * @extends {Component<Refs>}
 */
class WlVideoReel extends Component {
  requiredRefs = ['viewport', 'track'];

  /** Current logical position in px (unbounded; wrapped only at render time). */
  #position = 0;
  /** Current velocity in px per millisecond. */
  #velocity = 0;
  /** Width of one original tile set (the loop period), in px. */
  #setWidth = 0;
  /** Per-16.6ms decay factor applied to velocity above the target. */
  #friction = 0.92;

  /** Whether the gentle continuous auto-scroll is enabled. */
  #autoScroll = false;
  /** Constant auto-scroll velocity in px/ms (signed, drifts left). */
  #autoScrollVelocity = 0;
  /** Whether visible hosted videos should play on their own. */
  #autoplay = false;
  /** True when prefers-reduced-motion is set (no autoplay / no auto-scroll). */
  #reduced = false;

  /** @type {Element[]} The original (non-clone) tiles. */
  #originalItems = [];
  /** @type {Element[]} Appended clone tiles. */
  #clones = [];

  #raf = null;
  #lastTime = 0;
  #dragging = false;
  #pointerId = null;
  #lastX = 0;
  #lastMoveTime = 0;
  #moved = 0;
  /** True while a pointer hovers the viewport (pauses auto-scroll). */
  #hovering = false;

  /** Below this speed (px/ms) the roll is considered stopped. */
  #STOP = 0.004;
  /** Drag distance (px) past which a trailing click is suppressed. */
  #CLICK_THRESHOLD = 6;

  /** @type {IntersectionObserver | undefined} */
  #io;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    const { track, viewport } = this.refs;
    if (!track || !viewport) return;

    this.#originalItems = Array.from(track.children);
    if (this.#originalItems.length === 0) return;

    this.#reduced = prefersReducedMotion();
    this.#readConfig();

    // Suppress the native drag-ghost across the whole track.
    track.addEventListener('dragstart', this.#preventDefault);

    viewport.addEventListener('pointerdown', this.#onPointerDown);
    viewport.addEventListener('pointermove', this.#onPointerMove);
    viewport.addEventListener('pointerup', this.#onPointerUp);
    viewport.addEventListener('pointercancel', this.#onPointerUp);
    viewport.addEventListener('lostpointercapture', this.#onPointerUp);
    viewport.addEventListener('keydown', this.#onKeyDown);
    viewport.addEventListener('pointerenter', this.#onPointerEnter);
    viewport.addEventListener('pointerleave', this.#onPointerLeave);
    // Tap-to-play (hosted, autoplay off) and lazy embed activation.
    track.addEventListener('click', this.#onClick);

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(viewport);

    // Wait one frame so layout (fixed tile widths) settles before measuring.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#build();
      this.#observeVideos();
      if (this.#autoScroll) this.#start();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#resizeObserver?.disconnect();
    this.#io?.disconnect();

    const { viewport, track } = this.refs;
    track?.removeEventListener('dragstart', this.#preventDefault);
    track?.removeEventListener('click', this.#onClick);
    viewport?.removeEventListener('pointerdown', this.#onPointerDown);
    viewport?.removeEventListener('pointermove', this.#onPointerMove);
    viewport?.removeEventListener('pointerup', this.#onPointerUp);
    viewport?.removeEventListener('pointercancel', this.#onPointerUp);
    viewport?.removeEventListener('lostpointercapture', this.#onPointerUp);
    viewport?.removeEventListener('keydown', this.#onKeyDown);
    viewport?.removeEventListener('pointerenter', this.#onPointerEnter);
    viewport?.removeEventListener('pointerleave', this.#onPointerLeave);
  }

  #readConfig() {
    // Friction is tuned directly from the editor (0.85 snappy … 0.98 long glide).
    this.#friction = clamp(Number(this.dataset.friction ?? 0.92), 0.85, 0.98);

    // Reduced motion disables autoplay and auto-scroll, but keeps drag.
    this.#autoplay = this.dataset.autoplay === 'true' && !this.#reduced;
    this.#autoScroll = this.dataset.autoScroll === 'true' && !this.#reduced;

    const speed = Math.max(0, Number(this.dataset.autoScrollSpeed ?? 30)) / 1000; // px/ms
    this.#autoScrollVelocity = -speed; // drift left
  }

  // --- Layout / infinite loop ---

  #build() {
    this.#measure();
    this.#buildClones();
    this.#render();
  }

  #measure() {
    let width = 0;
    const gap = parseFloat(getComputedStyle(this.refs.track).columnGap) || 0;
    for (const item of this.#originalItems) {
      width += /** @type {HTMLElement} */ (item).offsetWidth;
    }
    // Each tile carries a trailing margin-inline-end (the gap), so the loop
    // period is the summed tile widths plus one gap per tile.
    const marginGap =
      parseFloat(getComputedStyle(/** @type {HTMLElement} */ (this.#originalItems[0])).marginInlineEnd) || gap;
    width += marginGap * this.#originalItems.length;
    this.#setWidth = width;
  }

  #buildClones() {
    for (const clone of this.#clones) clone.remove();
    this.#clones = [];

    if (this.#setWidth <= 0) return;

    const viewportWidth = this.refs.viewport.offsetWidth;
    // Total content must exceed viewport + 2×setWidth so a wrapped transform in
    // (-setWidth, 0] always leaves content covering the full viewport.
    const copies = Math.max(2, Math.ceil((viewportWidth + 2 * this.#setWidth) / this.#setWidth));

    const fragment = document.createDocumentFragment();
    for (let copy = 1; copy < copies; copy++) {
      for (const item of this.#originalItems) {
        const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.setAttribute('tabindex', '-1');
        // Clones must lazy-load independently of their source tile.
        for (const video of clone.querySelectorAll('.wl-video-reel__video')) {
          video.removeAttribute('data-loaded');
        }
        this.#clones.push(clone);
        fragment.appendChild(clone);
      }
    }
    this.refs.track.appendChild(fragment);
  }

  /** Apply the wrapped transform. The visible offset stays within (-setWidth, 0]. */
  #render() {
    if (this.#setWidth <= 0) {
      this.refs.track.style.transform = 'translate3d(0,0,0)';
      return;
    }
    let x = this.#position % this.#setWidth;
    if (x > 0) x -= this.#setWidth;
    this.refs.track.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  #handleResize = debounce(() => {
    if (!this.isConnected) return;
    this.#build();
    this.#observeVideos();
  }, 200);

  // --- Video lifecycle ---

  /** (Re)attach the IntersectionObserver to every tile (originals + clones). */
  #observeVideos() {
    this.#io?.disconnect();
    this.#io = new IntersectionObserver(this.#onIntersect, {
      root: this.refs.viewport,
      // Start loading/playing a little before the tile is fully on screen.
      rootMargin: '0px 100px',
      threshold: 0.1,
    });
    for (const video of this.refs.track.querySelectorAll('.wl-video-reel__video')) {
      this.#io.observe(video);
    }
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      const video = /** @type {HTMLVideoElement} */ (entry.target);
      if (entry.isIntersecting) {
        this.#ensureLoaded(video);
        if (this.#autoplay) this.#playVideo(video);
      } else {
        this.#pauseVideo(video);
      }
    }
  };

  /** Lazy-load: only fetch sources once a tile has neared the viewport. */
  #ensureLoaded(video) {
    if (video.dataset.loaded === 'true') return;
    const sources = video.querySelectorAll('source[data-src]');
    if (sources.length) {
      for (const source of sources) source.src = source.dataset.src;
      video.load();
    }
    video.dataset.loaded = 'true';
  }

  #playVideo(video) {
    this.#ensureLoaded(video);
    const result = video.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
    video.closest('.wl-video-reel__item')?.classList.add('is-playing');
  }

  #pauseVideo(video) {
    if (!video.paused) video.pause();
    video.closest('.wl-video-reel__item')?.classList.remove('is-playing');
  }

  // --- Animation (momentum + auto-scroll) ---

  #start() {
    if (this.#raf != null) return;
    this.#lastTime = performance.now();
    this.#raf = requestAnimationFrame(this.#frame);
  }

  #stop() {
    if (this.#raf != null) cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #frame = (now) => {
    const dt = Math.min(now - this.#lastTime, 64) || 16.667;
    this.#lastTime = now;

    // Auto-scroll only drifts while idle: pause on hover and during a drag.
    const drift = this.#autoScroll && !this.#hovering && !this.#dragging ? this.#autoScrollVelocity : 0;

    // Decay only the velocity in excess of the drift target, so momentum eases
    // into a steady drift (or to a stop when auto-scroll is off / paused).
    this.#velocity = drift + (this.#velocity - drift) * Math.pow(this.#friction, dt / 16.667);
    if (Math.abs(this.#velocity - drift) < this.#STOP) this.#velocity = drift;

    this.#position += this.#velocity * dt;
    this.#render();

    if (this.#velocity !== 0 || drift !== 0) {
      this.#raf = requestAnimationFrame(this.#frame);
    } else {
      this.#raf = null;
    }
  };

  // --- Pointer drag ---

  #onPointerEnter = () => {
    this.#hovering = true;
  };

  #onPointerLeave = () => {
    this.#hovering = false;
    // Resume the auto-scroll drift after the cursor leaves.
    if (this.#autoScroll) this.#start();
  };

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#dragging = true;
    this.#pointerId = event.pointerId;
    this.#lastX = event.clientX;
    this.#lastMoveTime = performance.now();
    this.#velocity = 0;
    this.#moved = 0;

    this.#stop();
    this.setAttribute('dragging', '');
    try {
      this.refs.viewport.setPointerCapture(event.pointerId);
    } catch {
      /* capture may fail on synthetic events; pointer listeners still fire */
    }
  };

  #onPointerMove = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId) return;

    const dx = event.clientX - this.#lastX;
    this.#lastX = event.clientX;
    this.#moved += Math.abs(dx);
    this.#position += dx;

    const now = performance.now();
    const elapsed = now - this.#lastMoveTime;
    if (elapsed > 0) {
      // Light smoothing keeps the release velocity stable against jitter.
      this.#velocity = this.#velocity * 0.7 + (dx / elapsed) * 0.3;
      this.#lastMoveTime = now;
    }

    this.#render();
  };

  #onPointerUp = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId) return;

    this.#dragging = false;
    this.#pointerId = null;
    this.removeAttribute('dragging');
    try {
      this.refs.viewport.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }

    // If the pointer stalled before release, treat it as no flick.
    if (performance.now() - this.#lastMoveTime > 80) this.#velocity = 0;

    // Suppress the click that follows a real drag so links/tap-to-play don't fire.
    if (this.#moved > this.#CLICK_THRESHOLD) {
      this.refs.track.addEventListener('click', this.#preventClick, { capture: true });
      setTimeout(() => {
        this.refs.track.removeEventListener('click', this.#preventClick, { capture: true });
      }, 0);
    }

    // Roll on (momentum), then ease into auto-scroll if enabled. Always start so
    // the loop can settle the row even from a standstill when auto-scroll is on.
    if (Math.abs(this.#velocity) > this.#STOP || this.#autoScroll) this.#start();
  };

  #onKeyDown = (event) => {
    let nudge = 0;
    if (event.key === 'ArrowRight') nudge = -0.6; // reveal items to the right
    else if (event.key === 'ArrowLeft') nudge = 0.6;
    else return;

    event.preventDefault();
    this.#velocity = nudge; // px/ms — friction turns the nudge into a short glide
    this.#start();
  };

  #onClick = (event) => {
    const item = /** @type {HTMLElement} */ (event.target).closest('.wl-video-reel__item');
    if (!item) return;
    // Link tiles navigate on click; never hijack them for play/pause.
    if (item.tagName === 'A') return;

    // YouTube/Vimeo: build and inject the iframe on first interaction.
    const embed = item.querySelector('.wl-video-reel__embed');
    if (embed && embed.dataset.loaded !== 'true') {
      this.#loadEmbed(/** @type {HTMLElement} */ (embed));
      return;
    }

    // Hosted video with autoplay off: tap toggles this tile, pausing siblings.
    const video = /** @type {HTMLVideoElement | null} */ (item.querySelector('.wl-video-reel__video'));
    if (video && !this.#autoplay) {
      if (video.paused) {
        for (const other of this.refs.track.querySelectorAll('.wl-video-reel__video')) {
          if (other !== video) this.#pauseVideo(other);
        }
        this.#playVideo(video);
      } else {
        this.#pauseVideo(video);
      }
    }
  };

  /** Replace an embed poster with its provider iframe (YouTube/Vimeo). */
  #loadEmbed(embed) {
    const id = embed.dataset.embedId;
    const type = embed.dataset.embedType;
    if (!id) return;

    const params = 'autoplay=1&playsinline=1';
    const src =
      type === 'vimeo'
        ? `https://player.vimeo.com/video/${id}?${params}&muted=1&loop=1`
        : `https://www.youtube.com/embed/${id}?${params}&mute=1&loop=1&playlist=${id}&rel=0`;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.className = 'wl-video-reel__iframe';
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', embed.getAttribute('aria-label') || 'Video');

    embed.dataset.loaded = 'true';
    embed.replaceChildren(iframe);
  }

  #preventClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  #preventDefault = (event) => event.preventDefault();
}

if (!customElements.get('wl-video-reel')) {
  customElements.define('wl-video-reel', WlVideoReel);
}
